import React, { useState } from 'react';
import { quotesService } from '../../services/quotesService';
import Modal from '../common/Modal';
import './PDFQuoteUploadModal.css';

const PDFQuoteUploadModal = ({ onClose, onQuoteDataExtracted }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    // Accept PDFs and images
    const acceptedTypes = [
      'application/pdf',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif',
      'image/bmp',
      'image/heic',
      'image/heif'
    ];
    
    if (!acceptedTypes.includes(file.type)) {
      setError('Please select a PDF or image file (PNG, JPG, HEIC)');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }
    
    setSelectedFile(file);
    setError(null);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const result = await quotesService.parsePDF(selectedFile);
      
      if (result.success) {
        onQuoteDataExtracted(result.data);
        onClose();
      } else {
        setError(result.error || 'Failed to parse file');
      }
    } catch (err) {
      console.error('File upload error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const footer = (
    <>
      <button
        className="btn-secondary"
        onClick={onClose}
        disabled={uploading}
      >
        Cancel
      </button>
      <button
        className="btn-primary"
        onClick={handleUpload}
        disabled={!selectedFile || uploading}
      >
        {uploading ? (
          <>
            <span className="spinner"></span>
            Processing PDF...
          </>
        ) : (
          <>
            <span>🤖</span>
            Parse with AI
          </>
        )}
      </button>
    </>
  );

  return (
    <Modal isOpen onClose={onClose} title="🤖 AI Quote Builder" maxWidth="600px" className="pdf-upload-modal" footer={footer}>
          <p className="modal-description">
            Upload a supplier quote PDF or screenshot and our AI will automatically extract the information 
            and create quote components for you to review.
          </p>

          <div 
            className={`file-drop-zone ${dragActive ? 'drag-active' : ''} ${selectedFile ? 'has-file' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {selectedFile ? (
              <div className="selected-file">
                <div className="file-icon">
                  {selectedFile.type.startsWith('image/') ? '📸' : '📄'}
                </div>
                <div className="file-info">
                  <div className="file-name">{selectedFile.name}</div>
                  <div className="file-size">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>
                <button 
                  className="remove-file-btn"
                  onClick={() => setSelectedFile(null)}
                  disabled={uploading}
                >
                  ✕
                </button>
              </div>
            ) : (
              <>
                <div className="drop-icon">📁</div>
                <p className="drop-text">
                  Drag and drop your PDF or image here, or
                </p>
                <label className="file-select-btn">
                  Choose File
                  <input
                    type="file"
                    accept=".pdf,image/png,image/jpeg,image/jpg,image/gif,image/bmp,image/heic,image/heif"
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                  />
                </label>
                <p className="file-requirements">
                  PDF or Image (PNG, JPG, HEIC) • Max 10MB
                </p>
              </>
            )}
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="supported-types">
            <h4>✨ Supported Quote Types:</h4>
            <div className="type-grid">
              <div className="type-item">🏨 Hotels</div>
              <div className="type-item">✈️ Flights</div>
              <div className="type-item">🚗 Car Rentals</div>
              <div className="type-item">🚢 Cruises</div>
              <div className="type-item">🎢 Disney Packages</div>
              <div className="type-item">🎡 Universal Packages</div>
              <div className="type-item">🎯 Activities</div>
              <div className="type-item">📦 Package Deals</div>
            </div>
          </div>
    </Modal>
  );
};

export default PDFQuoteUploadModal;
